<template>
    <!-- main container -->
    <v-container fluid class="ma-0 pa-0">
        <v-row dense>
            <!-- left pane -->
            <v-col cols="6">
                <v-container class="ma-0 py-6 pl-6 pr-3">
                    <!-- single left pane -->
                    <template v-if="record.isSingle">
                        <v-row dense>
                            <v-col cols="12">
                                <h4>{{ $t('datum_urhzeit') }}</h4>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6">
                                <LabelField :label="$t('start_date')">
                                    <DateTextfield
                                        :value="record.start_date"
                                        :placeholder="$t('select_date')"
                                        :rules="getRules('required')"
                                        hide-details="auto"
                                        @input="onInput('start_date', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="6">
                                <LabelField :label="$t('start')">
                                    <TimeTextfield
                                        :value="record.event_time_start"
                                        :placeholder="$t('time_placeholder')"
                                        :rules="timeIsMandatory ? getRules('required') : []"
                                        hide-details="auto"
                                        @input="onInput('event_time_start', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6" />
                            <v-col cols="6">
                                <LabelField :label="$t('end')">
                                    <TimeTextfield
                                        :value="record.event_time_end"
                                        :placeholder="$t('time_placeholder')"
                                        hide-details="auto"
                                        @input="onInput('event_time_end', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row v-if="showDoorOpeningClosingHoursFields" dense>
                            <v-col cols="6">
                                <LabelField :label="$t('opening_time_start')">
                                    <TimeTextfield
                                        :value="record.opening_time_start"
                                        :placeholder="$t('time_placeholder')"
                                        hide-details="auto"
                                        @input="onInput('opening_time_start', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="6">
                                <LabelField :label="$t('opening_time_end')">
                                    <TimeTextfield
                                        :value="record.opening_time_end"
                                        :placeholder="$t('time_placeholder')"
                                        hide-details="auto"
                                        @input="onInput('opening_time_end', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row v-if="showEventLocationFieldOnLeftSide" dense>
                            <v-col cols="12">
                                <LabelField :label="$t('location')">
                                    <LocationSelectField
                                        :location-id="record.location_id"
                                        :organizer-id="event.organizer_id"
                                        :placeholder="$t('placeholder_event_info_categories_location_id')"
                                        @selected="onEventLocationSelect($event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row v-if="showOnlineLocationUrlField" dense>
                            <v-col cols="12">
                                <LabelField
                                    :label="$t('online_event')"
                                    :label-tip="$t('online_event_help')"
                                    class="mt-5"
                                >
                                    <UrlField
                                        :value="record.online_event_url"
                                        :placeholder="$t('placeholder_event_info_categories_online_event_url')"
                                        :rules="[isKulturstream ? getRule('required') : null].filter(Boolean)"
                                        @input="onInput('online_event_url', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                    </template>
                    <!-- recurring left pane -->
                    <template v-if="record.isRecurring">
                        <v-row dense>
                            <v-col cols="12">
                                <h4>{{ $t('serienmuster') }}</h4>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12">
                                <LabelField :label="$t('frequency')">
                                    <v-radio-group
                                        :value="record.recurring_type"
                                        :rules="getRules('required')"
                                        row
                                        hide-details="auto"
                                        class="mt-0 pt-0"
                                        @change="onInput('recurring_type', $event)"
                                    >
                                        <v-radio
                                            :value="Schedule.RECURRING_TYPE_DAILY"
                                            :label="$t('recurring_type_daily')"
                                        />
                                        <v-radio
                                            :value="Schedule.RECURRING_TYPE_WEEKLY"
                                            :label="$t('recurring_type_weekly')"
                                        />
                                        <v-radio
                                            :value="Schedule.RECURRING_TYPE_MONTHLY"
                                            :label="$t('recurring_type_monthly')"
                                        />
                                        <v-radio
                                            :value="Schedule.RECURRING_TYPE_YEARLY"
                                            :label="$t('recurring_type_yearly')"
                                        />
                                    </v-radio-group>
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="5">
                                <v-text-field
                                    :value="record.recur_every_n"
                                    :rules="getRules('required')"
                                    type="number"
                                    min="1"
                                    class="recur-every-n-field"
                                    :prefix="recurringPrefix"
                                    :suffix="recurringSuffix"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    clearable
                                    @input="onInput('recur_every_n', $event)"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="record.recurring_type === Schedule.RECURRING_TYPE_WEEKLY" dense>
                            <v-col cols="5" class="d-flex">
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('monday_short')"
                                    :value="1"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('tuesday_short')"
                                    :value="2"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('wednesday_short')"
                                    :value="3"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('thursday_short')"
                                    :value="4"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('friday_short')"
                                    :value="5"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('saturday_short')"
                                    :value="6"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                                <v-checkbox
                                    :input-value="record.recurrence_config.weekdays"
                                    class="mt-0 mr-8"
                                    :label="$t('sunday_short')"
                                    :value="7"
                                    @change="onInput('recurrence_config.weekdays', $event)"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12">
                                <h4>{{ $t('seriendauer') }}</h4>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6">
                                <LabelField :label="$t('start_date')">
                                    <DateTextfield
                                        :value="record.start_date"
                                        :placeholder="$t('select_date')"
                                        :rules="[...getRules('required')]"
                                        hide-details="auto"
                                        @input="onInput('start_date', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="6">
                                <LabelField :label="$t('end_date')">
                                    <DateTextfield
                                        :value="
                                            record.start_date?.isAfter(record.end_date)
                                                ? record.start_date
                                                : record.end_date
                                        "
                                        :placeholder="$t('select_date')"
                                        :rules="[...getRules('required')]"
                                        hide-details="auto"
                                        :value-from="
                                            record.start_date?.isValid()
                                                ? record.start_date.add(1, 'day').format('YYYY-MM-DD')
                                                : undefined
                                        "
                                        @input="onInput('end_date', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12">
                                <h4>{{ $t('zeiten') }}</h4>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6">
                                <LabelField :label="$t('start')">
                                    <TimeTextfield
                                        :value="record.event_time_start"
                                        :placeholder="$t('time_placeholder')"
                                        :rules="timeIsMandatory ? getRules('required') : []"
                                        hide-details="auto"
                                        @input="onInput('event_time_start', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="6">
                                <LabelField :label="$t('end')">
                                    <TimeTextfield
                                        :value="record.event_time_end"
                                        :placeholder="$t('time_placeholder')"
                                        hide-details="auto"
                                        @input="onInput('event_time_end', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row v-if="showDoorOpeningClosingHoursFields" dense>
                            <v-col cols="6">
                                <LabelField :label="$t('opening_time_start')">
                                    <TimeTextfield
                                        :value="record.opening_time_start"
                                        :placeholder="$t('time_placeholder')"
                                        hide-details="auto"
                                        @input="onInput('opening_time_start', $event)"
                                    />
                                </LabelField>
                            </v-col>
                            <v-col cols="6">
                                <LabelField :label="$t('opening_time_end')">
                                    <TimeTextfield
                                        :value="record.opening_time_end"
                                        :placeholder="$t('time_placeholder')"
                                        hide-details="auto"
                                        @input="onInput('opening_time_end', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                    </template>
                    <EditAdditionalInfoContainer
                        class="mt-5"
                        :event="event"
                        :record="record"
                        :is-recuring-exhibition="isRecuringExhibition"
                    />
                </v-container>
            </v-col>

            <!-- right pane -->
            <v-col cols="6">
                <v-container class="ma-0 mt-5 pb-6 pt-1 pl-3 pr-6 alternative-container">
                    <v-row dense>
                        <v-col cols="12">
                            <h4>{{ $t('alternative_angaben') }}</h4>
                        </v-col>
                    </v-row>
                    <v-row v-if="showEventLocationField" dense>
                        <v-col cols="12">
                            <LabelField :label="$t('location')">
                                <LocationSelectField
                                    :location-id="record.location_id"
                                    :organizer-id="event.organizer_id"
                                    :placeholder="$t('placeholder_event_info_categories_location_id')"
                                    @selected="onEventLocationSelect($event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <v-container fluid class="ma-0 pa-0">
                                <v-row v-if="showTargetAudienceField" dense>
                                    <v-col cols="6">
                                        <LabelField :label="$t('label_schedule_target_audience_id')">
                                            <TargetAudienceSelect
                                                :value="record.target_audience_id"
                                                :placeholder="$t('placeholder_schedule_target_audience_id')"
                                                hide-details="auto"
                                                clearable
                                                @input="onInput('target_audience_id', $event)"
                                            />
                                        </LabelField>
                                    </v-col>
                                    <v-col v-if="showTypicalAgeRangeField" cols="6">
                                        <LabelField :label="$t('label_schedule_typical_age_range')">
                                            <v-textarea
                                                :value="record.typical_age_range"
                                                :placeholder="$t('placeholder_schedule_typical_age_range')"
                                                dense
                                                outlined
                                                hide-details="auto"
                                                @input="onInput('typical_age_range', $event)"
                                            />
                                        </LabelField>
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col v-if="showPresalesField" cols="6">
                                        <LabelField :label="$t('label_schedule_presale')">
                                            <MultiItemContainer :show-type-field="false" item-class="mb-2" single>
                                                <template #text>
                                                    <v-textarea
                                                        :value="record.presales"
                                                        :placeholder="$t('placeholder_schedule_presale')"
                                                        rows="2"
                                                        dense
                                                        outlined
                                                        hide-details="auto"
                                                        @input="onInput('presales', $event)"
                                                    />
                                                </template>
                                                <template #default>
                                                    <UrlField
                                                        :value="record.presales_link"
                                                        :placeholder="$t('placeholder_schedule_presale_link')"
                                                        @input="onInput('presales_link', $event)"
                                                    />
                                                </template>
                                            </MultiItemContainer>
                                        </LabelField>
                                    </v-col>
                                    <v-col v-else cols="6"></v-col>
                                    <v-col v-if="showTicketpricesField" cols="6">
                                        <LabelField :label="$t('label_schedule_ticketprices')">
                                            <v-textarea
                                                :value="record.ticketprices"
                                                :placeholder="$t('placeholder_schedule_ticketprices')"
                                                dense
                                                outlined
                                                hide-details="auto"
                                                @input="onInput('ticketprices', $event)"
                                            />
                                        </LabelField>
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col v-if="showDescriptionField" cols="6">
                                        <LabelField :label="$t('label_schedule_description')">
                                            <v-textarea
                                                :value="record.description"
                                                :placeholder="$t('placeholder_schedule_description')"
                                                dense
                                                outlined
                                                hide-details="auto"
                                                @input="onInput('description', $event)"
                                            />
                                        </LabelField>
                                    </v-col>
                                    <v-col v-if="showAccessibilityField" cols="6">
                                        <LabelField :label="$t('label_schedule_accessibility')">
                                            <v-textarea
                                                :value="record.accessibility"
                                                :placeholder="$t('placeholder_schedule_accessibility')"
                                                dense
                                                outlined
                                                hide-details="auto"
                                                @input="onInput('accessibility', $event)"
                                            />
                                        </LabelField>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { userIsStaff } from '@/helpers/auth';
import { Schedule } from '@/models/Schedule';
import { Event } from '@/models/Event';
import { LocationCollection } from '@/models/Location';
import EditAdditionalInfoContainer from '@/components/schedule/EditAdditionalInfoContainer.vue';
import getRulesMixin from '@/mixins/get-form-rules';
import TargetAudienceSelect from '@/components/target_audience/select.vue';
import MultiItemContainer from '@/components/multi_item/Container.vue';
import LocationSelectField from '@/components/location/SelectField.vue';

export default {
    name: 'ScheduleEditDialogMainContainer',
    components: {
        TargetAudienceSelect,
        MultiItemContainer,
        EditAdditionalInfoContainer,
        LocationSelectField,
    },
    mixins: [getRulesMixin],

    props: {
        event: Event,
        record: Schedule,
    },

    data() {
        const eventLocationCollection = new LocationCollection();
        if (!this.userIsStaff()) {
            eventLocationCollection.addExtraParam('showAllLocations', true);
        }
        return {
            eventLocationCollection,
        };
    },

    computed: {
        isRecuringExhibition() {
            return this.event.event_class === Event.CLASS_EXHIBITION && this.record.isRecurring;
        },

        recurringPrefix() {
            switch (this.record.recurring_type) {
                case Schedule.RECURRING_TYPE_DAILY:
                    return this.$tc('jeden_alle', this.record.recur_every_n);
                case Schedule.RECURRING_TYPE_WEEKLY:
                    return this.$tc('jede_alle', this.record.recur_every_n);
                case Schedule.RECURRING_TYPE_MONTHLY:
                    return this.$tc('jeden_alle', this.record.recur_every_n);
                case Schedule.RECURRING_TYPE_YEARLY:
                    return this.$tc('jedes_alle', this.record.recur_every_n);
                default:
                    return this.$tc('jeden_alle', this.record.recur_every_n);
            }
        },

        recurringSuffix() {
            switch (this.record.recurring_type) {
                case Schedule.RECURRING_TYPE_DAILY:
                    return this.$tc('day', this.record.recur_every_n);
                case Schedule.RECURRING_TYPE_WEEKLY:
                    return this.$tc('week', this.record.recur_every_n);
                case Schedule.RECURRING_TYPE_MONTHLY:
                    return this.$tc('month', this.record.recur_every_n);
                case Schedule.RECURRING_TYPE_YEARLY:
                    return this.$tc('year', this.record.recur_every_n);
                default:
                    return this.$tc('day', this.record.recur_every_n);
            }
        },

        showDoorOpeningClosingHoursFields() {
            // refs #25821
            if (
                this.event.event_class === Event.CLASS_EVENT ||
                this.event.event_class === Event.CLASS_TOURNEE ||
                (this.event.event_class === Event.CLASS_EXHIBITION && !this.record.isRecurring)
            ) {
                return true;
            }

            return false;
        },

        showEventLocationField() {
            if (this.isRecuringExhibition) {
                return false;
            }
            if ([Event.CLASS_CINEMA, Event.CLASS_EXHIBITION, Event.CLASS_TOURNEE].includes(this.event.event_class)) {
                return false;
            }
            return true;
        },

        showEventLocationFieldOnLeftSide() {
            if (this.event.event_class === Event.CLASS_TOURNEE) {
                return true;
            }

            return false;
        },

        showOnlineLocationUrlField() {
            if (this.event.event_class === Event.CLASS_TOURNEE) {
                return true;
            }
            return false;
        },

        showTargetAudienceField() {
            if (this.isRecuringExhibition) {
                return false;
            }

            return true;
        },

        showTypicalAgeRangeField() {
            if (this.isRecuringExhibition) {
                return false;
            }

            return true;
        },

        showTicketpricesField() {
            if (this.isRecuringExhibition) {
                return false;
            }

            return true;
        },

        showDescriptionField() {
            if (this.isRecuringExhibition) {
                return false;
            }

            return true;
        },

        showAccessibilityField() {
            if (this.isRecuringExhibition) {
                return false;
            }

            return true;
        },

        showPresalesField() {
            if (this.isRecuringExhibition) {
                return false;
            }
            if (this.event.event_class === Event.CLASS_CINEMA) {
                return false;
            }
            return true;
        },

        timeIsMandatory() {
            // see #25357: Time is not mandatory for recurring dates:
            if (this.record.isRecurring) {
                return false;
            }

            // Exhibitions: time fields are optional:
            if ([Event.CLASS_EXHIBITION, Event.CLASS_PERMANENT_EXHIBITION].includes(this.event.event_class)) {
                return false;
            }
            return true;
        },

        isKulturstream() {
            return this.record?.event_class === Event.CLASS_STREAM;
        },
    },

    created() {
        this.Event = Event;
        this.Schedule = Schedule;
    },

    methods: {
        userIsStaff,

        onInput(field, value) {
            const path = field.split('.');
            let currentField = null;
            let object = this.record;
            while ((currentField = path.shift())) {
                if (path.length > 0) {
                    object = object[currentField];
                } else {
                    object[currentField] = value;
                }
            }
        },

        onEventLocationSelect(record) {
            // Nach dem event-location-Select setzen wir sowohl den Display-Wert (damit man gleich sieht
            // was gewählt wurde), sowie die ID:
            if (record) {
                this.onInput('location_id', record.id);
                this.onInput('location_title', record.title);
            } else {
                this.onInput('location_id', null);
                this.onInput('location_title', null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.alternative-container {
    background-color: var(--tgk-grey-5);
}
.recur-every-n-field {
    :deep(.v-text-field__prefix) {
        min-width: 47px;
        margin-right: 5px;
    }
    :deep(.v-text-field__suffix) {
        min-width: 62px;
    }
}
</style>
